/**
 * The share viewer's sidebar-panel header (#757).
 *
 * Outside a PDApp it is the plain "Explorer" label it always was. In PDApp mode
 * it becomes the workspace's PDApp header — label "PDApp" plus Run app / App
 * settings — mirroring WorkspaceView's panel header (same icons, same titles
 * and aria-labels, same CSS-uppercased label) so the two surfaces read as one
 * product. (The workspace's "Run as app" — a chromeless window of its own — is
 * deliberately not offered here: the share viewer IS the app's window.)
 *
 * What each control does HERE (the share-view equivalents, no new authority):
 *   • Run app      → the APP TAB: the app running full-bleed, exactly the face
 *                    `requestMiniappView(id, 'run')` selects in MiniAppEditor —
 *                    through the share session's existing app-principal +
 *                    consent path. Disabled when the app has no index.html.
 *   • App settings → the SETTINGS TAB (MiniAppEditor's 'settings' face), shown
 *                    in place. Only rendered when the chrome resolved that the
 *                    signed-in recipient natively manages this PDApp — see
 *                    `pdappCanManage`.
 */
import { Play, Settings, RotateCw } from 'lucide-react'
import type { PdappFace } from './pdapp'

export interface SharePdappMode {
  /** The PDApp folder the viewer is in PDApp mode for. */
  folderId: string
  /** The app's display name (the `.pdapp` folder name), when known. */
  name: string | null
  /** The share root IS this PDApp (vs a PDApp nested inside a shared folder). */
  isRoot: boolean
  /** The entry lookup has resolved (Run's enabled state is meaningful). */
  entryReady: boolean
  /** The app's index.html id, or null when it has none. */
  entryId: string | null
  /** The entry lookup FAILED (403 / 5xx / network) — distinct from "has no
   *  index.html": the recipient gets a retry, not a false "nothing to run". */
  entryError?: boolean
  /** Re-run the entry lookup after `entryError`. */
  onRetryEntry?: () => void
  /** Which PDApp tab is showing (null ⇒ a plain file from the panel). */
  face: PdappFace | null
  /** What the viewer slot shows BENEATH the tabs: 'run' ⇒ the app runtime (the
   *  app tab, or Settings opened over it); null ⇒ the open file's editor (no
   *  face, or Settings opened over the editor — it keeps what it covers). */
  viewerFace?: 'run' | null
  onRun: () => void
  /** Null ⇒ Settings is hidden (no native manage access). */
  onSettings: (() => void) | null
  /** The identity Settings acts as — the share chrome's EFFECTIVE tenant + org
   *  email (URL override, else best access), the same one the gate asked under.
   *  NOT necessarily the account picker's ✓ (that follows the bound app
   *  session), which is why the tab names it (see SharePdappSettings). */
  settingsIdentity?: { tenantId: string | null; orgEmail: string | null; tenantName?: string | null }
  /** Leave PDApp mode back to the folder listing — nested PDApps only. */
  onExit: (() => void) | null
}

const btn = 'p-1 themed-text-muted hover:themed-text hover:themed-bg-tertiary rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent'
/** The button for the PDApp tab currently showing. */
const btnOn = 'p-1 themed-text themed-bg-tertiary rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'

export function PdappPanelHeader({ pdapp }: { pdapp: SharePdappMode | null }) {
  const noEntry = !!pdapp && pdapp.entryReady && !pdapp.entryId
  const runDisabled = !pdapp || !pdapp.entryReady || !pdapp.entryId
  const runTitle = pdapp?.entryError
    ? "Couldn't load this PDApp"
    : noEntry ? 'This PDApp has no index.html to run' : 'Run app'
  return (
    <div className="h-10 border-b themed-border flex items-center px-3 text-xs themed-text-muted uppercase tracking-wide shrink-0">
      <span className="flex-1" data-testid="share-sidebar-label">{pdapp ? 'PDApp' : 'Explorer'}</span>
      {pdapp && (
        <div className="flex items-center gap-0.5 normal-case">
          {pdapp.entryError && pdapp.onRetryEntry && (
            // The lookup failed — the header is the one place a retry is
            // reachable on a direct sub-file session (no empty state there).
            <button
              type="button"
              onClick={pdapp.onRetryEntry}
              className={btn}
              title="Couldn't load this PDApp — retry"
              aria-label="Retry loading PDApp"
            >
              <RotateCw size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={pdapp.onRun}
            disabled={runDisabled}
            className={pdapp.face === 'run' ? btnOn : btn}
            title={runTitle}
            aria-label="Run app"
            aria-pressed={pdapp.face === 'run'}
          >
            <Play size={14} />
          </button>
          {pdapp.onSettings && (
            <button
              type="button"
              onClick={pdapp.onSettings}
              className={pdapp.face === 'settings' ? btnOn : btn}
              title="App settings"
              aria-label="App settings"
              aria-pressed={pdapp.face === 'settings'}
            >
              <Settings size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The viewer's content when PDApp mode is up with NO file open — the recipient
 * closed the running app, or the app has no index.html to land on. Names the
 * two ways forward (Run, or a file from the panel) and, for a PDApp nested in a
 * shared folder, the way back out.
 */
export function PdappEmptyState({ pdapp }: { pdapp: SharePdappMode }) {
  const hasEntry = !!pdapp.entryId
  const failed = !!pdapp.entryError
  return (
    <div className="flex max-w-sm flex-col items-center gap-3 text-center" data-testid="share-pdapp-empty">
      <p className="text-sm themed-text-muted">
        {failed
          ? "Couldn't load this PDApp. Check your connection and retry."
          : hasEntry
            ? 'Run the app, or open one of its files from the PDApp panel.'
            : 'This PDApp has no index.html to run. Open one of its files from the PDApp panel.'}
      </p>
      <div className="flex items-center gap-2">
        {failed && pdapp.onRetryEntry && (
          <button
            type="button"
            onClick={pdapp.onRetryEntry}
            className="themed-btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap"
          >
            Retry
          </button>
        )}
        {hasEntry && (
          <button
            type="button"
            onClick={pdapp.onRun}
            className="themed-btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap"
          >
            <Play size={12} />
            Run app
          </button>
        )}
        {pdapp.onExit && (
          <button
            type="button"
            onClick={pdapp.onExit}
            className="themed-btn-secondary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap"
          >
            Back to folder
          </button>
        )}
      </div>
    </div>
  )
}
